/**
 * Workspace page - 2-column layout for story browsing and editing
 * Story 198: View and filter stories in backlog
 *
 * Column 1: StoryBrowser (resizable) - browse/search/filter stories
 * Column 2: Placeholder (flex) - story detail panel (Story 199)
 *
 * Story 564: when the address carries `settings`, the project settings page is
 * shown instead of the two columns. Leaving removes only that parameter, so the
 * columns come back with the same story, tab and filters.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { StoryBrowser } from '../components/workspace/StoryBrowser';
import { StoryDetailPanel } from '../components/workspace/StoryDetailPanel';
import { ResizableDivider, getStoredWidth } from '../components/workspace/ResizableDivider';
import { setLastProject, setLastStory } from '../App';
import { useUpdatesStream } from '../hooks/useUpdatesStream';
import { createInvalidationDispatcher, handleReconnection } from '../lib/cacheInvalidation';
import { DOC_TAB_PREFIX, type TabId } from '../components/workspace/StoryTabs';
import { ProjectSettingsView } from '../components/settings/ProjectSettingsView';
import { SETTINGS_SECTIONS } from '../components/settings/settingsSections';
import { SETTINGS_PARAM, withSettingsOpen, withoutSettings } from '../components/settings/settingsUrl';
import { SETTINGS_ENTRY_ATTRIBUTE } from '../components/workspace/ProjectSettingsCog';
import { AiServiceAlert } from '../components/workspace/AiServiceAlert';

/** Story 546: the settings section the AI Service alert opens. */
const AI_SERVICES_SECTION_ID = 'ai-services';
import './Workspace.css';

/** Fixed tab IDs recognized without doc: prefix */
const FIXED_TABS = new Set<string>(['prd', 'design', 'tasks', 'updates']);

/** Track previous value of a state for comparison */
function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Validate tab ID from URL query parameter.
 * Story 297: Accepts fixed tabs and doc:<slug> dynamic tabs.
 */
function isValidTab(tab: string | null): tab is TabId {
  if (!tab) return false;
  if (FIXED_TABS.has(tab)) return true;
  if (tab.startsWith(DOC_TAB_PREFIX)) return true;
  return false;
}

export function Workspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const projectSlug = searchParams.get('project');

  // Read story, tab, section from URL
  const selectedStoryId = searchParams.get('story');
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = isValidTab(tabParam) ? tabParam : 'prd';
  const selectedSection = searchParams.get('section');

  // Read search and status filter from URL
  const searchQuery = searchParams.get('q') || '';
  const statusParam = searchParams.get('status');
  // Default to proposed,in-progress,qa if not specified (Story 274)
  const statusFilter = statusParam ? statusParam.split(',') : ['proposed', 'in-progress', 'qa'];

  // Story 564: which settings section is shown, when settings are open
  const settingsParam = searchParams.get(SETTINGS_PARAM);
  const settingsOpen = settingsParam !== null;

  const [columnWidth, setColumnWidth] = useState(() => getStoredWidth());

  // Create debounced cache invalidation dispatcher with cleanup
  // Story 253: Centralized cache invalidation on WebSocket events
  const invalidationDispatcher = useMemo(() => {
    if (!projectSlug) return undefined;
    return createInvalidationDispatcher(queryClient, projectSlug);
  }, [queryClient, projectSlug]);

  // Cleanup dispatcher timer when dependencies change or unmount
  useEffect(() => {
    return () => {
      invalidationDispatcher?.cleanup();
    };
  }, [invalidationDispatcher]);

  // Subscribe to real-time updates at workspace level
  const {
    events: realtimeEvents,
    connectionStatus,
    subscribedProjectId,
    lastError,
    clearEvents,
    reconnect,
  } = useUpdatesStream(projectSlug, {
    onEvent: invalidationDispatcher?.dispatch,
  });

  // Handle reconnection - invalidate all caches since we may have missed events
  const prevConnectionStatus = usePrevious(connectionStatus);
  useEffect(() => {
    if (
      prevConnectionStatus === 'reconnecting' &&
      connectionStatus === 'connected' &&
      projectSlug
    ) {
      handleReconnection(queryClient, projectSlug);
    }
  }, [connectionStatus, prevConnectionStatus, queryClient, projectSlug]);

  // Redirect to dashboard if no project specified
  useEffect(() => {
    if (!projectSlug) {
      navigate('/dashboard');
    }
  }, [projectSlug, navigate]);

  // Remember last visited project and story for quick access after sign-in
  useEffect(() => {
    if (projectSlug) {
      setLastProject(projectSlug);
    }
  }, [projectSlug]);

  useEffect(() => {
    if (selectedStoryId) {
      setLastStory(selectedStoryId);
    }
  }, [selectedStoryId]);

  // Handle story selection - update URL
  const handleSelectStory = useCallback((storyExternalId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('story', storyExternalId);
      // Clear tab and section when selecting a new story
      next.delete('tab');
      next.delete('section');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle tab change - update URL
  const handleTabChange = useCallback((tab: TabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'prd') {
        next.delete('tab'); // PRD is default
      } else {
        next.set('tab', tab);
      }
      // Clear section when changing tabs
      next.delete('section');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle section selection - update URL (replace to avoid cluttering history with scroll positions)
  const handleSectionSelect = useCallback((sectionKey: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('section', sectionKey);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle cross-document navigation (e.g., PRD → Design, Tasks → Design)
  // This pushes to history so back button works
  const handleNavigate = useCallback((tab: TabId, sectionKey?: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'prd') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      if (sectionKey) {
        next.set('section', sectionKey);
      } else {
        next.delete('section');
      }
      return next;
    }); // No replace: true - push to history for back button
  }, [setSearchParams]);

  const handleResize = useCallback((width: number) => {
    setColumnWidth(width);
  }, []);

  // Handle search query change - update URL
  const handleSearchChange = useCallback((query: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (query) {
        next.set('q', query);
      } else {
        next.delete('q');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Handle status filter change - update URL
  const handleStatusChange = useCallback((statuses: string[]) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (statuses.length > 0) {
        next.set('status', statuses.join(','));
      } else {
        next.delete('status');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Story 564: opening settings pushes a history entry, so the browser's Back
  // button leaves settings for where the owner was.
  const handleOpenSettings = useCallback(() => {
    setSearchParams((prev) => withSettingsOpen(prev, SETTINGS_SECTIONS[0].id));
  }, [setSearchParams]);

  // Story 546: the alert's link opens settings on AI Services.
  const handleOpenAiServices = useCallback(() => {
    setSearchParams((prev) => withSettingsOpen(prev, AI_SERVICES_SECTION_ID));
  }, [setSearchParams]);

  // Choosing a section replaces the entry, so Back leaves settings in one step.
  const handleSelectSettingsSection = useCallback((sectionId: string) => {
    setSearchParams((prev) => withSettingsOpen(prev, sectionId), { replace: true });
  }, [setSearchParams]);

  const handleCloseSettings = useCallback(() => {
    setSearchParams((prev) => withoutSettings(prev));
  }, [setSearchParams]);

  // Leaving settings, by Back to workspace or the browser, gives focus back to
  // the control that opens them.
  const wasSettingsOpen = usePrevious(settingsOpen);
  useEffect(() => {
    if (wasSettingsOpen && !settingsOpen) {
      document.querySelector<HTMLElement>(`[${SETTINGS_ENTRY_ATTRIBUTE}]`)?.focus();
    }
  }, [wasSettingsOpen, settingsOpen]);

  // Don't render workspace without project
  if (!projectSlug) {
    return null;
  }

  if (settingsOpen) {
    return (
      <ProjectSettingsView
        projectSlug={projectSlug}
        sectionParam={settingsParam}
        onSelectSection={handleSelectSettingsSection}
        onBack={handleCloseSettings}
      />
    );
  }

  return (
    <div className="workspace">
      <div className="workspace__column-1" style={{ width: columnWidth, minWidth: columnWidth }}>
        <StoryBrowser
          projectSlug={projectSlug}
          selectedStoryId={selectedStoryId}
          onSelectStory={handleSelectStory}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          statusFilter={statusFilter}
          onStatusChange={handleStatusChange}
          onOpenSettings={handleOpenSettings}
          alert={projectSlug ? <AiServiceAlert projectSlug={projectSlug} onOpen={handleOpenAiServices} /> : undefined}
        />
      </div>
      <ResizableDivider onResize={handleResize} initialWidth={columnWidth} />
      <div className="workspace__column-2">
        {selectedStoryId ? (
          <StoryDetailPanel
            projectSlug={projectSlug}
            storyId={selectedStoryId}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            selectedSection={selectedSection}
            onSectionSelect={handleSectionSelect}
            onNavigate={handleNavigate}
            realtimeEvents={realtimeEvents}
            connectionStatus={connectionStatus}
            subscribedProjectId={subscribedProjectId}
            connectionError={lastError}
            onReconnect={reconnect}
            onClearEvents={clearEvents}
          />
        ) : (
          <div className="workspace__empty-state">
            Select a story from the left
          </div>
        )}
      </div>
    </div>
  );
}
